/**
 * Phase 78g — Allocate / Reassign confirmation modal.
 *
 * Used wherever admin is about to assign a trip to a pilot:
 *   - Trip Monitoring → Allocate button
 *   - Pilot Trips     → Reassign button
 *
 * Flow:
 *   1. Caller decides the target `pilotId` and passes it + the `trip`.
 *   2. Modal opens, fetches the pilot's current queue (in-progress +
 *      allocated) via `/api/admin/pilot-trips-monitor`.
 *   3. Admin picks one of two paths:
 *        a. "Allocate as separate trip" — calls PUT /trips/{id}/allocate
 *           with `{pilot_id}` only.
 *        b. Click any queue row — calls PUT /trips/{id}/allocate
 *           with `{pilot_id, pool_into: <row.id>}` so the new trip
 *           becomes a pooled child of that lead.
 *   4. On success, calls `onDone(result)` so the parent can refresh.
 *   5. "Back" button returns to pilot selection (callable parent),
 *      "Cancel" closes the modal without action.
 *
 * Warnings (per UX 1d):
 *   - amber banner when a pool target is already in-progress.
 *   - amber banner when a pool target has a different pickup OR drop
 *     supplier (unusual pool but allowed at admin's discretion).
 */
import React, { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import {
  Truck, Path, Package, Warning, Stack, CheckCircle, ArrowLeft, X,
} from '@phosphor-icons/react';

const STATUS_BADGE = {
  allocated:      'bg-[#A855F7]/10 text-[#A855F7]',
  accepted:       'bg-[#A855F7]/10 text-[#A855F7]',
  pending:        'bg-[#FF8C00]/10 text-[#FF8C00]',
  in_progress:    'bg-[#002FA7]/10 text-[#002FA7]',
  pickup_reached: 'bg-[#FFD700]/15 text-[#B8860B]',
  pickup_done:    'bg-[#FF8C00]/10 text-[#FF8C00]',
  drop_reached:   'bg-[#FF8C00]/10 text-[#FF8C00]',
};

function fmtItems(t) {
  if (Array.isArray(t.items) && t.items.length) {
    return t.items.filter(i => i?.name).map(i => `${i.name} ${i.qty || ''}${i.uom || ''}`).join(' · ');
  }
  if (t.item) return `${t.item} ${t.qty || 0}${t.uom || ''}`;
  return '-';
}

export default function AllocateConfirmModal({
  open,
  onClose,    // close without allocating
  onBack,     // optional — when set, shows a "Back" button to re-pick pilot
  onDone,     // (result) => void called after successful allocation
  trip,       // the trip being allocated/reassigned
  pilotId,
  pilotName,  // optional — used for header
}) {
  const [queue, setQueue] = useState(null);    // {in_process, allocated} or null
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Fetch pilot's current queue.
  useEffect(() => {
    if (!open || !pilotId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setQueue(null);
    (async () => {
      try {
        const { data } = await api.get('/admin/pilot-trips-monitor', { params: { pilot_id: pilotId } });
        if (cancelled) return;
        setQueue({
          in_process: data.in_process || [],
          allocated: data.allocated || [],
          pilot: data.pilot,
        });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load pilot queue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, pilotId]);

  // Filter the pool candidates: every queued trip except this one or
  // anything that is itself a pooled stub. Best matches (same pickup
  // AND same drop) are sorted to the top so admin spots them instantly.
  const candidates = useMemo(() => {
    if (!queue || !trip) return [];
    const all = [...queue.in_process, ...queue.allocated];
    const filtered = all.filter(r => r.id !== trip.id && !r.is_pooled_into);
    const isBestMatch = (c) =>
      c.pickup_supplier_name === trip.pickup_supplier_name &&
      c.drop_supplier_name === trip.drop_supplier_name;
    return filtered.slice().sort((a, b) => {
      const aBest = isBestMatch(a) ? 0 : 1;
      const bBest = isBestMatch(b) ? 0 : 1;
      return aBest - bBest;
    });
  }, [queue, trip]);

  const doAllocate = async (pool_into) => {
    if (!trip || !pilotId) return;
    setBusy(true);
    setError('');
    try {
      const body = pool_into ? { pilot_id: pilotId, pool_into } : { pilot_id: pilotId };
      const { data } = await api.put(`/trips/${trip.id}/allocate`, body);
      onDone?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Allocation failed');
    } finally {
      setBusy(false);
    }
  };

  if (!trip) return null;

  const hasQueue = candidates.length > 0;
  const tripFrom = trip.pickup_supplier_name;
  const tripTo = trip.drop_supplier_name;

  // No-queue branch: act as a confirmation modal so admin always sees a
  // popup before allocation, matching the requested UX (#2-b).
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="rounded-none max-w-2xl p-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="allocate-confirm-modal">
        <DialogHeader className="px-5 py-4 border-b border-[#E5E5E5] bg-[#0A0A0A] text-white space-y-1">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-sm uppercase tracking-widest text-[#00C853] font-bold">Allocate Trip</DialogTitle>
            <button onClick={() => !busy && onClose()} className="text-white/70 hover:text-white" data-testid="alloc-modal-close-x"><X size={20} weight="bold" /></button>
          </div>
          <p className="text-base font-bold leading-tight">
            #{trip.id} · {tripFrom} <span className="text-[#666] font-normal">→</span> {tripTo}
          </p>
          <p className="text-xs text-[#999]">
            <Package size={11} className="inline mr-1" />{fmtItems(trip)}
          </p>
          <p className="text-xs text-[#FFD700] font-bold pt-1">→ {pilotName || 'Selected pilot'}</p>
        </DialogHeader>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && <p className="text-center text-[#666] text-sm py-6">Loading pilot queue…</p>}
          {error && <p className="bg-[#FF3333]/10 text-[#FF3333] text-xs px-3 py-2 border border-[#FF3333]/30 mb-3" data-testid="alloc-modal-error">{error}</p>}

          {!loading && queue && (
            <>
              {!hasQueue ? (
                <div className="border border-dashed border-[#E5E5E5] bg-[#F7F7F7] px-4 py-6 text-center" data-testid="alloc-modal-empty-queue">
                  <CheckCircle size={28} className="text-[#00C853] mx-auto mb-2" />
                  <p className="text-sm font-bold">Pilot has no other trips</p>
                  <p className="text-xs text-[#666] mt-1">This trip will be allocated as standalone.</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const bestMatchCount = candidates.filter(c =>
                      c.pickup_supplier_name === tripFrom && c.drop_supplier_name === tripTo
                    ).length;
                    return (
                      <p className="text-[11px] uppercase tracking-widest font-bold text-[#666] mb-2" data-testid="alloc-modal-candidates-hint">
                        Pool into one of pilot's existing trips
                        {bestMatchCount > 0 && (
                          <span className="text-[#00C853] ml-2 normal-case tracking-normal font-normal">
                            ({bestMatchCount} best match{bestMatchCount > 1 ? 'es' : ''} found)
                          </span>
                        )}
                      </p>
                    );
                  })()}
                  <div className="space-y-2" data-testid="alloc-modal-candidate-list">
                    {candidates.map(c => {
                      const inProgress = ['in_progress', 'pickup_reached', 'pickup_done', 'drop_reached'].includes(c.status);
                      const diffPickup = c.pickup_supplier_name !== tripFrom;
                      const diffDrop = c.drop_supplier_name !== tripTo;
                      // Best match = same pickup AND same drop. These are the
                      // safest pools — highlight in green so admin can pick
                      // confidently without scanning every row.
                      const bestMatch = !diffPickup && !diffDrop;
                      const warn = !bestMatch && (inProgress || diffPickup || diffDrop);
                      // Card border / background priority: best > warn > default.
                      const cardCls = bestMatch
                        ? 'border-[#00C853]/60 bg-[#00C853]/8 hover:border-[#00C853] hover:bg-[#00C853]/12'
                        : warn
                          ? 'border-[#FF8C00]/40 bg-[#FF8C00]/5 hover:border-[#002FA7] hover:bg-[#002FA7]/5'
                          : 'border-[#E5E5E5] bg-white hover:border-[#002FA7] hover:bg-[#002FA7]/5';
                      return (
                        <button
                          key={c.id}
                          onClick={() => doAllocate(c.id)}
                          disabled={busy}
                          className={`w-full text-left border ${cardCls} transition-colors px-3 py-2 disabled:opacity-50`}
                          data-testid={`alloc-modal-pool-${c.id}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {bestMatch ? (
                              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#00C853] text-white font-bold inline-flex items-center gap-1" data-testid={`alloc-modal-best-${c.id}`}>
                                <CheckCircle size={11} weight="fill" />Best Match
                              </span>
                            ) : (
                              <Stack size={14} weight="bold" className="text-[#A855F7]" />
                            )}
                            <span className="font-mono text-xs font-bold">#{c.id}</span>
                            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 font-bold ${STATUS_BADGE[c.status] || 'bg-[#808080]/10 text-[#808080]'}`}>{c.status?.replace(/_/g, ' ')}</span>
                            {warn && <Warning size={12} weight="fill" className="text-[#FF8C00]" />}
                          </div>
                          <p className="text-sm font-bold leading-tight mt-1">{c.pickup_supplier_name}</p>
                          <p className="text-xs text-[#666] leading-tight"><Path size={10} className="inline mr-1" />{c.drop_supplier_name}</p>
                          {fmtItems(c) !== '-' && (
                            <p className="text-[11px] text-[#666] mt-1"><Package size={10} className="inline mr-1" />{fmtItems(c)}</p>
                          )}
                          {bestMatch && inProgress && (
                            <p className="text-[10px] mt-1 text-[#FF8C00] font-bold">⚠ Trip in progress — pool will be visible to pilot but DC photo may already be taken</p>
                          )}
                          {warn && (
                            <p className="text-[10px] mt-1 text-[#FF8C00] font-bold">
                              {inProgress && '⚠ Trip in progress'}
                              {(inProgress && (diffPickup || diffDrop)) ? ' · ' : ''}
                              {diffPickup && 'Different pickup'}
                              {(diffPickup && diffDrop) ? ' · ' : ''}
                              {diffDrop && 'Different drop'}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-[#E5E5E5] bg-[#F7F7F7] flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            {onBack && (
              <Button
                variant="outline"
                onClick={() => !busy && onBack()}
                disabled={busy}
                className="rounded-none"
                data-testid="alloc-modal-back-btn"
              >
                <ArrowLeft size={14} className="mr-1" />Back
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => !busy && onClose()}
              disabled={busy}
              className="rounded-none"
              data-testid="alloc-modal-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <Button
            onClick={() => doAllocate(null)}
            disabled={busy || loading}
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none"
            data-testid="alloc-modal-separate-btn"
          >
            <Truck size={14} className="mr-1" />
            {busy ? 'Allocating…' : 'Allocate as separate trip'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
